import { ToastService } from 'root/shared/services/toast-service/toast.service';
import { Component, OnInit, ViewChild, OnDestroy, inject, ChangeDetectionStrategy, isDevMode } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription, skip } from 'rxjs';
import { FrontendContextService, ContactUsService, LocalizationService, GlobalConfigService, BroadcastService, ResponsiveEvent } from 'root/services';
import { DialogComponent } from 'root/components';
import { environment } from 'root/environment';
import { MySandvikFeatures, PermissionService } from 'root/services/permission';
import { animate, style, transition, trigger } from '@angular/animations';
import { MibpSessionService } from 'root/services/mibp-session/mibp-session.service';
import { RouterEventService } from 'root/services/router/router-event.service';
import { NewContactUsTopic, SupportCaseFormData } from 'root/components/contact-dialog/new-contact-us-form/new-contact-us-form.types';
import { ActiveMibpToast } from 'root/shared/services/toast-service/toast.service.interface';
import { ToastType } from 'root/shared/services/toast-service/toast.enum';

import { MibpSupportTeamToolbarComponent } from 'root/components/support-team-toolbar/support-team-toolbar.component';
import { AddtocartDialogComponent } from 'root/components/addtocart-dialog/addtocart-dialog.component';
import { MibpSalesforceEmbeddedServiceComponent } from 'root/components/salesforce-embedded-service/salesforce-embedded-service.component';
import { CartQuickviewComponent } from 'root/components/cart-quickview/cart-quickview.component';
import { MibpGoogleMapsScriptComponent } from 'root/pages/application-root/google-maps-script.component';
import { UnhandledExceptionDialogComponent } from 'root/components/unhandled-exception-dialog/unhandled-exception-dialog.component';
import { MibpDevopsFeedbackComponent } from 'root/components/devops-feedback/devops-feedback.component';
import { ResourceStringEditorComponent } from 'root/components/resource-string-editor/resource-string-editor.component';
import { NewContactUsFormComponent } from 'root/components/contact-dialog/new-contact-us-form/new-contact-us-form.component';
import { SystemMessageDialogComponent } from 'root/components/system-message-dialog/system-message-dialog.component';
import { MibpFeatureTourComponent } from 'root/components/feature-tour/feature-tour.component';
import { MibpBrowserUpdateNoticeComponent } from 'root/components/browser-update-notice/browser-update-notice.component';
import { MibpHeaderComponent } from 'root/components/navigation/header/header.component';
import { MibpSidebarComponent } from 'root/components/navigation/sidebar/sidebar.component';
import { TermsAndConditionsComponent } from 'root/platform/components/legal/terms-and-conditions/terms-and-conditions.component';
import { SystemMessageComponent } from 'root/components/system-message/system-message.component';
import { MibpBreadcrumbsV2Component } from 'root/components/navigation/breadcrumbs/breadcrumbs-v2.component';
import { MibpFooterV2Component } from 'root/components/navigation/footer/footer-v2.component';
import { EcommerceRouterLinkDirective } from 'root/directives/ecommerce-router-link/ecommerce-router-link.directive';
import { permissionPolicyKey } from 'root/services/permission';
import { MibpApplicationGateComponent } from 'root/platform/components/application-gate/application-gate.component';


@Component({
  templateUrl: './application-root.component.html',
  styleUrls: ['./application-root.component.scss'],
  animations: [trigger('myToggleTrigger', [
    transition(':enter', [
      style({ transform: 'translateX(-100%)' }),
      animate('300ms', style({ transform: 'translateX(0)' })),
    ]),
    transition(':leave', [
      animate('300ms', style({ transform: 'translateX(-100%)' }))
    ])
  ])],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AddtocartDialogComponent,
    CartQuickviewComponent,
    DialogComponent,
    EcommerceRouterLinkDirective,
    MibpApplicationGateComponent,
    MibpBreadcrumbsV2Component,
    MibpBrowserUpdateNoticeComponent,
    MibpDevopsFeedbackComponent,
    MibpFeatureTourComponent,
    MibpFooterV2Component,
    MibpGoogleMapsScriptComponent,
    MibpHeaderComponent,
    MibpSalesforceEmbeddedServiceComponent,
    MibpSidebarComponent,
    MibpSupportTeamToolbarComponent,
    NewContactUsFormComponent,
    ResourceStringEditorComponent,
    RouterOutlet,
    SystemMessageComponent,
    SystemMessageDialogComponent,
    TermsAndConditionsComponent,
    UnhandledExceptionDialogComponent
]
})

export class ApplicationRootPageComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private globalConfig = inject(GlobalConfigService);
  private localizationService = inject(LocalizationService);
  private frontEndContextService = inject(FrontendContextService);
  private contactService = inject(ContactUsService);
  private broadcastService = inject(BroadcastService);
  private permissionService = inject(PermissionService);
  private sessionService = inject(MibpSessionService);
  private routerEventSerice = inject(RouterEventService);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);


  activeToast: ActiveMibpToast;
  toasterSubscription: Subscription;
  lang = '';
  sub: Subscription;
  enableResourceStringEditor = false;
  enableBugReport = environment.enableBugReport;
  sidebarsize = 290;
  @ViewChild('newContactDialog', { static: true }) newContactDialog: DialogComponent;
  public newContactUsFormData: SupportCaseFormData;
  private contactUsOpenValue: boolean;
  isTestingFeatureManagement = false;
  isSupportTeamMember = false;
  toggleSidebar = true;
  sessionSub?: Subscription;
  newContactDialogSub: Subscription;
  private showNewContactUsItemEnquiryForm: boolean;
  get contactUsOpen(): boolean {
    return this.contactUsOpenValue;
  }
  set contactUsOpen(value: boolean) {
    this.contactUsOpenValue = value;
    if (this.contactUsOpenValue && this.newContactDialog) {
      this.newContactDialog.open();
    } else if (!this.contactUsOpenValue && this.newContactDialog) {
      this.newContactDialog.close();
    }
  }

  sidebarToggleSub: Subscription;
  isLoggedIn = false;
  isSidebarHidden = true;
  layoutSub: Subscription;
  responsiveSub: Subscription;
  hasConfigurationErrors = false;
  headerSize = 75;
  isFocusContent = false;
  hideGlobalLayout = false;
  canSeeCart : boolean;
  actAsSub: Subscription;
  focusContentSub: Subscription;

  constructor() {
    this.isLoggedIn = this.sessionService.hasActiveUser();
    this.responsiveSub = this.broadcastService.responsiveBreakpoint.subscribe(e => this.onResponsiveUpdate(e));
    this.isTestingFeatureManagement = this.broadcastService.snapshot.globalConfig.enableFeatureTestNotice;
    this.isSupportTeamMember = this.permissionService.test({ isSupportTeamMember: true });
    this.broadcastService.hasConfigurationErrors.subscribe(hasErrors => this.hasConfigurationErrors = hasErrors);
    this.showNewContactUsItemEnquiryForm = this.sessionService.current?.user?.enabledFeatures?.includes(MySandvikFeatures.ContactUsFormItemEnquiry);
    this.testCartPermission();
  }

  private onResponsiveUpdate(e: ResponsiveEvent): void {

    if (e.lteq('xs')) {
      if (this.headerSize !== 105) {
        this.headerSize = 105;
      }
    } else {
      if (this.headerSize !== 75) {
        this.headerSize = 75;
      }
    }

  }

  showTabs(): boolean {
    if (this.router.url.indexOf('/dashboard') !== -1) {
      return false;
    } else {
      return true;
    }
  }

  private userChanged(): void {
    this.isSidebarHidden = false;

    if (this.isLoggedIn && !this.globalConfig.disableToggleSideBar) {
      const sidebarStatus = localStorage.getItem('mibp.mysandvik-sidebar.collapsed');
      if (sidebarStatus !== null) {
        this.isSidebarHidden = sidebarStatus === 'true' ? true : false;
        this.broadcastService.setSidebarCollapsed(this.isSidebarHidden);
      }
      //TODO: Analyse the above logic whether it's needed in any scenario
      // this.frontEndContextService.sidebarToggleSubject.subscribe(x=>{
      //   this.toggleSideBar(x);
      // });
    }
    else {
      this.isSidebarHidden = true;
    }

    // Hide sidebar if there is no user
    if (!this.sessionService.hasActiveUser()) {
      this.isSidebarHidden = true;
    }
  }


  ngOnInit(): void {
    this.actAsSub = this.broadcastService.deliverySequence.pipe(skip(1)).subscribe({
      next: () => this.onDeliverySequenceChanged()
    });

    this.handleFocusContent();
    this.updateGlobalLayout();
    this.layoutSub = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => this.updateGlobalLayout());

    this.sessionSub = this.sessionService.current$.subscribe(() => this.userChanged());
    this.isLoggedIn = this.sessionService.isLoggedIn();

    this.lang = this.localizationService.getLang();

    if (this.sessionService.isLoggedIn()) {
      this.enableResourceStringEditor = this.frontEndContextService.testPermission(permissionPolicyKey('canManageTranslations'));
      this.newContactDialogSub = this.contactService.openNewContactUsDialog.subscribe(value => {
        this.newContactUsFormData = value.formData;
        this.contactUsOpen = value.open;
      });
    }

    if (this.isLoggedIn) {
      this.routerEventSerice.subscribeToRouterEvents();
    }

    if (this.isLoggedIn && !this.globalConfig.disableToggleSideBar) {
      this.sidebarToggleSub = this.frontEndContextService.sidebarToggleSubject.subscribe(x => {
        this.toggleSideBar(x);
      });
    }
  }

  handleFocusContent(): void {
    const focusQueryParam = this.route.snapshot.queryParams['focus'];

    if (focusQueryParam && focusQueryParam == 'content') {
      this.isFocusContent = true;
    }

    this.focusContentSub = this.broadcastService.focusContent.pipe(skip(1)).subscribe({
      next: (result) => this.isFocusContent = result
    });
  }

  private updateGlobalLayout(): void {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let routeSupportsRegistrationLayout = false;

    while (route) {
      if (route.data['hideGlobalLayout'] === true) {
        routeSupportsRegistrationLayout = true;
      }
      route = route.firstChild;
    }

    const previewEnabled = isDevMode()
      && this.route.snapshot.queryParamMap.get('registrationLayoutPreview') === 'true';
    this.hideGlobalLayout = routeSupportsRegistrationLayout
      && (this.globalConfig.enableNewUserRegistrationPageLayout === true || previewEnabled);
  }

  onDeliverySequenceChanged(): void {
    this.testCartPermission();
  }

  testCartPermission(): void {
    this.canSeeCart = this.permissionService.test(permissionPolicyKey('canSeeCart'));
  }

  closeForm(success: any): void {

    if(success == undefined){
      const dialogTitleResourceKey = this.newContactUsFormData.newContactUsTopic == NewContactUsTopic.RequestUpgradeQuote ?
        'SupportCase_MachineUpgrade_SubmittedSuccessfullyTitle' : 'SupportCase_SubmittedSuccessfully';
      const dialogMessageResourceKey = this.newContactUsFormData.newContactUsTopic == NewContactUsTopic.RequestUpgradeQuote ?
        'SupportCase_MachineUpgrade_SubmittedSuccessfullyMessage' : null;

      this.activeToast = this.toastService.showWithTitle(dialogMessageResourceKey, dialogTitleResourceKey, {
        isHtml: true,
        type: ToastType.Success,
        disableTimeOut: false
      });
      this.newContactUsFormData = null;
      this.contactUsOpen = false;
      this.contactService.closeNewContactUs();
    }
    else if (!success) {
      this.activeToast = this.toastService.showWithTitle(null, 'ContactUs_Message_Sent_Error', {
        isHtml: false,
        type: ToastType.Error,
        disableTimeOut: false
      });
    }
    else {

      this.newContactUsFormData = null;
      this.contactUsOpen = false;
      this.contactService.closeNewContactUs();

      this.activeToast = this.toastService.showWithTitle('SupportCase_SubmittedSuccessfullyMessage', 'SupportCase_SubmittedSuccessfully', {
        isHtml: true,
        type: ToastType.Success,
        disableTimeOut: false,
        buttons: [
          { id: 'redirect', textResourceString: 'Feature_general_view_supportcases_name' },

        ]
      });
    }
    if (this.activeToast.onButtonClick) {
      this.toasterSubscription = this.activeToast.onButtonClick.subscribe(res => {
        if (res.id == 'redirect') {
          this.router.navigate([`/${this.frontEndContextService.getLang()}/support/support-cases`]);
          this.toastService.removeToast(this.activeToast.toastId);
        }
      });
    }
  }




  onContactUsFormClosed(): void {
    this.newContactUsFormData = null;
    this.contactUsOpen = false;
    this.contactService.closeNewContactUs();
  }

  toggleSideBar(toggle: boolean): void {
    if (toggle) {
      this.isSidebarHidden = !this.isSidebarHidden;
      localStorage.setItem('mibp.mysandvik-sidebar.collapsed', `${this.isSidebarHidden}`);
      this.broadcastService.setSidebarCollapsed(this.isSidebarHidden);
      this.frontEndContextService.isSidebarHiddenSubject.next({ isHidden: this.isSidebarHidden, number: this.sidebarsize });
    }
  }

  ngOnDestroy(): void {
    this.layoutSub?.unsubscribe();
    this.responsiveSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
    this.sidebarToggleSub?.unsubscribe();
    this.actAsSub?.unsubscribe();
    if( this.toasterSubscription){
      this.toasterSubscription.unsubscribe();
    }
  }
}
